/**
 * Paint walk: retained tree → batched quads, painter's order (tree order), physical pixels.
 * Handles pixel snapping (edges snapped independently — adjacent rects stay gap-free),
 * multiplicative opacity inheritance, and the overflow:hidden scissor stack.
 * Text glyphs join in UI-3; image/9-slice in the texture pipeline task.
 */
import { layoutText, type GlyphQuad } from '../text/layoutText';
import { resolveTextFont } from '../text/MsdfFont';
import type { UIDocument } from '../tree/UIDocument';
import type { UINode } from '../tree/UINode';
import type { Color4, GradientDecl, TransformOrigin } from '../types';
import { fromTransform, multiply, type Mat2D } from './mat2d';
import type { UIScissorRect } from './UIBatch';

const DEFAULT_ORIGIN: TransformOrigin = { x: 0.5, y: 0.5 };

/** The slice of UIBatch the walk needs — mockable in tests. */
export interface UIPaintTarget {
  setScissor(rect: UIScissorRect | null): void;
  rect(x: number, y: number, w: number, h: number, color: Color4, cornerRadius?: number, borderWidth?: number): void;
  /**
   * Backdrop blur cut (UI-16): replace the node's rounded-rect footprint with the blurred
   * backbuffer (+ acrylic grain) BEFORE the node paints. Optional — hosts without the
   * capability (or mock targets) simply skip and the translucent bg still renders.
   */
  backdropRect?(
    x: number,
    y: number,
    w: number,
    h: number,
    cornerRadius: number,
    blurPx: number,
    noise: number,
    opacity: number,
  ): void;
  /**
   * UI material rect (UI-19): the material shader replaces the plain background fill. color =
   * backgroundColor∙opacity (the material's tint input); falls back to a plain rect while the
   * host is still resolving the compiled WGSL. Optional for mock targets.
   */
  materialRect?(
    x: number,
    y: number,
    w: number,
    h: number,
    color: Color4,
    cornerRadius: number,
    borderWidth: number,
    materialGuid: string,
    time: number,
  ): void;
  /** Box-shadow layer. Outer = under the node (painted first); inset = inner glow over the fills. */
  shadowRect?(
    x: number,
    y: number,
    w: number,
    h: number,
    cornerRadius: number,
    offsetX: number,
    offsetY: number,
    blur: number,
    spread: number,
    color: Color4,
    inset?: boolean,
  ): void;
  /** Linear-gradient fill (CSS wave 2) — layers OVER the color fill, under the image. */
  gradientRect?(
    x: number,
    y: number,
    w: number,
    h: number,
    cornerRadius: number,
    gradient: GradientDecl,
    opacity: number,
  ): void;
  /** Background image (CSS wave 2): stretch or 9-slice; insets = src texels / dest physical px. */
  imageRect?(
    x: number,
    y: number,
    w: number,
    h: number,
    tint: Color4,
    cornerRadius: number,
    imageGuid: string,
    srcInsets: readonly [number, number, number, number] | null,
    destInsets: readonly [number, number, number, number] | null,
  ): void;
  /**
   * Set the affine transform (UI-17) applied to subsequent quad corner POSITIONS (CPU-side — no
   * batch break; local/SDF params stay untransformed). null = identity. Optional: mock targets
   * without transform support omit it and the walk still paints (untransformed).
   */
  setTransform?(m: Mat2D | null): void;
  /**
   * MSDF glyph run: quads in LOGICAL px relative to (originX, originY) (PHYSICAL px, already
   * snapped); the target scales quads by `scale`. distanceRange = font pxRange for the shader.
   */
  glyphRun(
    quads: readonly GlyphQuad[],
    originX: number,
    originY: number,
    scale: number,
    color: Color4,
    fontGuid: string,
    distanceRange: number,
  ): void;
}

export interface UIPaintOptions {
  /** logical → physical multiplier (canvas scaler output). */
  scale: number;
  /** Snap rect edges to physical pixels (canvas `pixelPerfect`). Default true. */
  pixelSnap?: boolean;
}

function withAlpha(c: Color4, alpha: number): Color4 {
  return alpha === 1 ? c : [c[0], c[1], c[2], c[3] * alpha];
}

function intersect(a: UIScissorRect | null, b: UIScissorRect): UIScissorRect {
  // Always a fresh object so the caller can attach a corner radius without mutating shared state.
  // The rounded-clip radius is intentionally NOT carried across an intersection: only the innermost
  // rounded container's corners are honored (nested rounded clips fall back to rectangular for the
  // outer one — a rare case the caller re-attaches radius for the container it's clipping).
  if (!a) return { x: b.x, y: b.y, width: b.width, height: b.height };
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  return { x, y, width: Math.max(0, right - x), height: Math.max(0, bottom - y) };
}

export function paintDocument(target: UIPaintTarget, doc: UIDocument, options: UIPaintOptions): void {
  const snap = options.pixelSnap !== false;
  for (const child of doc.root.children) {
    paintNode(target, doc, child, 1, null, options.scale, snap, null);
  }
  target.setScissor(null);
  target.setTransform?.(null);
}

function paintNode(
  target: UIPaintTarget,
  doc: UIDocument,
  node: UINode,
  inheritedOpacity: number,
  scissor: UIScissorRect | null,
  scale: number,
  snap: boolean,
  transform: Mat2D | null,
): void {
  if (!node.isElement) return; // rawtext is drawn by its <text> parent (UI-3)
  const style = node.style;
  if (style.display === 'none') return;

  const opacity = inheritedOpacity * (style.opacity ?? 1);
  if (opacity <= 0) return;

  // A transform anywhere up the chain (or on this node) forces UNSNAPPED geometry: the batch
  // transforms exact corners, and per-corner pixel snapping would distort a rotated/scaled quad.
  const tf = style.transform;
  const transformed = transform !== null || (tf != null && tf.length > 0);
  const doSnap = snap && !transformed;

  // logical → physical, snapping EDGES (not origin+size) so adjacent rects don't gap
  const rx = node.layoutX * scale;
  const ry = node.layoutY * scale;
  const rr = (node.layoutX + node.layoutW) * scale;
  const rb = (node.layoutY + node.layoutH) * scale;
  const x = doSnap ? Math.round(rx) : rx;
  const y = doSnap ? Math.round(ry) : ry;
  const w = (doSnap ? Math.round(rr) : rr) - x;
  const h = (doSnap ? Math.round(rb) : rb) - y;

  const radius = (style.borderRadius ?? 0) * scale;
  const borderWidth = style.borderWidth ?? 0;
  const bw = borderWidth > 0 ? (doSnap ? Math.max(1, Math.round(borderWidth * scale)) : borderWidth * scale) : 0;
  const clips = style.overflow === 'hidden' || style.overflow === 'scroll';

  // Compose this node's own transform (around transform-origin, in the UNtransformed physical box)
  // under the inherited one; the subtree paints in this cumulative frame.
  let cumulative = transform;
  if (tf && tf.length > 0) {
    const own = fromTransform(tf, style.transformOrigin ?? DEFAULT_ORIGIN, x, y, w, h, scale);
    cumulative = cumulative ? multiply(cumulative, own) : own;
  }

  const drawBorder = (): void => {
    const borderColor = style.borderColor;
    if (bw > 0 && borderColor && borderColor[3] > 0) {
      target.setScissor(scissor);
      target.setTransform?.(cumulative);
      target.rect(x, y, w, h, withAlpha(borderColor, opacity), radius, bw);
    }
  };

  // Set once for all of THIS node's own draws (children reset it, drawBorder re-sets it). Always
  // called — even null — so a transformed sibling's leftover matrix can't bleed into this node.
  target.setTransform?.(cumulative);

  if (w > 0 && h > 0) {
    const shadows = style.shadow;
    // OUTER shadows first, under everything. CSS lists topmost first → paint reversed so it lands on top.
    if (shadows && shadows.length && target.shadowRect) {
      for (let i = shadows.length - 1; i >= 0; i--) {
        const sh = shadows[i]!;
        if (sh.inset || sh.color[3] <= 0) continue;
        target.setScissor(scissor);
        target.shadowRect(x, y, w, h, radius, sh.x * scale, sh.y * scale, sh.blur * scale, sh.spread * scale, withAlpha(sh.color, opacity), false);
      }
    }

    const backdropBlur = style.backdropBlur ?? 0;
    if (backdropBlur > 0 && target.backdropRect) {
      target.setScissor(scissor);
      target.backdropRect(x, y, w, h, radius, backdropBlur * scale, style.backdropNoise ?? 0, opacity);
    }

    const bg = style.backgroundColor;
    if (style.material && target.materialRect) {
      // the material IS the fill — it draws even without a bg color (white tint default)
      target.setScissor(scissor);
      target.materialRect(x, y, w, h, withAlpha(bg ?? [1, 1, 1, 1], opacity), radius, bw, style.material, doc.time);
    } else {
      // CSS layering: color at the bottom, then gradient, then image
      if (bg && bg[3] > 0) {
        target.setScissor(scissor);
        target.rect(x, y, w, h, withAlpha(bg, opacity), radius, 0);
      }
      if (style.gradient && target.gradientRect) {
        target.setScissor(scissor);
        target.gradientRect(x, y, w, h, radius, style.gradient, opacity);
      }
      if (style.backgroundImage && target.imageRect) {
        const slice = style.slice ?? null;
        const destInsets = slice
          ? ([slice[0] * scale, slice[1] * scale, slice[2] * scale, slice[3] * scale] as const)
          : null;
        target.setScissor(scissor);
        target.imageRect(
          x,
          y,
          w,
          h,
          withAlpha(style.tint ?? [1, 1, 1, 1], opacity),
          radius,
          style.backgroundImage,
          slice,
          destInsets,
        );
      }
    }

    // INSET shadows paint OVER the fills (inner glow / recess), under the content and border.
    if (shadows && shadows.length && target.shadowRect) {
      for (let i = shadows.length - 1; i >= 0; i--) {
        const sh = shadows[i]!;
        if (!sh.inset || sh.color[3] <= 0) continue;
        target.setScissor(scissor);
        target.shadowRect(x, y, w, h, radius, sh.x * scale, sh.y * scale, sh.blur * scale, sh.spread * scale, withAlpha(sh.color, opacity), true);
      }
    }

    // NOTE: the border paints AFTER the children (bottom of paintNode) — deliberately not
    // CSS z-order. Edge snapping is independent per rect, so at fractional scales a child's
    // snapped edge (and its half-pixel AA skirt) can land ON the ring's inner row; the ring
    // drawn last covers both that and the fill-vs-inner-curve radius mismatch. The trade-off
    // (a descendant intentionally overlapping its ancestor's border gets covered by the ring)
    // doesn't occur in in-flow layouts — the layout border keeps children inside the ring.

    // MSDF text content (UI-3). Glyph quads are laid out in logical units against the node's
    // content width — the same call the Yoga measure fn made, so paint matches layout.
    if (node.elementType === 'text') {
      const content = node.textContent();
      if (content) {
        const textStyle = doc.effectiveTextStyle(node);
        const resolved = resolveTextFont(textStyle);
        const color = (doc.inherited(node, 'color') ?? [1, 1, 1, 1]) as Color4;
        if (resolved && color[3] > 0) {
          const layout = layoutText(resolved.font, content, {
            fontSize: textStyle.fontSize,
            lineHeight: textStyle.lineHeight,
            letterSpacing: textStyle.letterSpacing,
            align: textStyle.textAlign,
            maxWidth: node.layoutW > 0 ? node.layoutW : Infinity,
            wrap: textStyle.whiteSpace !== 'nowrap',
            ellipsis: node.style.textOverflow === 'ellipsis', // not inherited (CSS parity)
          });
          target.setScissor(scissor);
          target.glyphRun(layout.quads, x, y, scale, withAlpha(color, opacity), resolved.guid, resolved.font.distanceRange);
        }
      }
    }
  }

  let childScissor = scissor;
  if (clips) {
    // CSS: overflow clips at the PADDING BOX — inset by the (layout-taking) border so scrolled
    // content can never paint over the border ring (which is then drawn on top, staying crisp).
    childScissor = intersect(scissor, {
      x: x + bw,
      y: y + bw,
      width: Math.max(0, w - 2 * bw),
      height: Math.max(0, h - 2 * bw),
    });
    // Rounded container → carry the padding-box corner radius (outer radius minus the border it
    // sits inside) so the shaders' uiClip carves the corners; the hardware scissor above still
    // bounds the rect. radius 0 leaves it a plain rectangular clip (no per-fragment cost).
    if (radius > 0) childScissor.radius = Math.max(0, radius - bw);
  }
  for (const c of node.children) paintNode(target, doc, c, opacity, childScissor, scale, snap, cumulative);

  if (w > 0 && h > 0) drawBorder();
}
